import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

// CSS
import "./roleCreationPage.css";
import "../0PageStyles.css";

// Components
import InputField from "../../Components/Other Components/inputField";
import InputGroup from "../../Components/Other Components/inputGroup";
import Button from "../../Components/Other Components/button";
import PageLoader from "../../Components/Other Components/pageLoader";
import PermissionView from "../../Components/User Components/PermissionView";
import PopupConfirmation from "../../Components/Other Components/popupConfirmation";
import WarehousePicker from "../../Components/Warehouse Components/warehousePicker";
import Title from "../../Components/Other Components/Title";
import MandatoryExplanation from "../../Components/Other Components/mandatoryExplanation";
import PageWrapper from "../pageWrapper";

// Functions and hooks
import {
  EditRole,
  DeleteRole,
  GetRoleData,
} from "../../BackendAccess/RoleHooks";
import { useTranslation } from "react-i18next";
import { fetchWithTimeout } from "../../hooks/useFetchWithTimeout";
import useFormStates from "../../hooks/useFormStates";
import { useNotification } from "../../Contexts/NotificationContext";
import { useErrorHandler } from "../../hooks/errorHandler";
import { usePopup } from "../../Contexts/PopupContext";

function RoleEditPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pageError, setPageError] = useState(false);

  const [editing, setEditing] = useState(false);
  const [deleteBtnDisabled, setDeleteBtnDisabled] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { showNotification } = useNotification();
  const { id: currentID } = location.state;

  const { formData, setField, loadInitialData, hasChanged } = useFormStates({});

  useErrorHandler(error, showNotification, setPageError);

  const { t } = useTranslation();

  const { showPopup, removePopup } = usePopup();

  const showConfirmationPopup = () => {
    const key = Date.now();
    showPopup(
      <PopupConfirmation
        style="warning"
        message={
          <p>
            {t("Are you sure you want to delete this role?", { ns: "roles" })}
            <br />
            {t(
              "This action will remove the role from every user that has this role and they will lose the permission to perform any action!",
              { ns: "roles" }
            )}
            <br />
            <span className="text-negative">
              {t("This action cannot be undone.")}
            </span>
          </p>
        }
        onConfirm={() => handleDelete(key)}
        onCancel={() => removePopup(key)}
      />,
      key
    );
  };

  useEffect(() => {
    fetchInitialValues();
  }, []);

  const fetchInitialValues = async () => {
    setLoading(true);

    const [data, error] = await fetchWithTimeout(() => GetRoleData(currentID));
    if (error) {
      error.isPageError = true;
      setError(error);
    } else {
      console.log(
        "Setting perms initial in role edit page: ",
        data.Permissions
      );
      loadInitialData({
        ...data,
        Permissions: [...data.Permissions],
        AllowedWarehouses: [...data.AllowedWarehouses],
        LastEditedBy:
          data.FirstName + " " + data.LastName + " (" + data.LastEditedBy + ")",
      });
    }
    setLoading(false);
  };

  const handleChange = (field, value) => {
    if (field === "PriorityLevel") {
      if (/^\d*$/.test(value)) {
        setField(field, value);
      } else {
        setError({
          err: t("validation.The Priority Level must be a positive number.", {
            ns: "error",
          }),
          errCode: 400,
        });
      }
    } else setField(field, value);
  };

  const OnApply = async () => {
    showNotification(t("Editing role...", { ns: "roles" }), "info");
    setEditing(true);
    try {
      const permissionsToPass = [...formData.Permissions].filter(
        (item) => item.HasPermission
      );
      const [data, error] = await EditRole(
        currentID,
        formData.RoleID,
        formData.Name,
        formData.PriorityLevel,
        formData.Color,
        permissionsToPass,
        formData.AllowedWarehouses
      );
      if (error) {
        setError(error);
      } else {
        showNotification(
          t("Role edited successfully!", { ns: "roles" }),
          "success"
        );
        navigate(-1);
      }
    } catch (err) {
      setError({
        err: t("server.Server error.", { ns: "error" }),
        errCode: 500,
      });
    } finally {
      setEditing(false);
    }
  };

  const handleDelete = async (key) => {
    setDeleteBtnDisabled(true);
    showNotification(t("Deleting item...", { ns: "roles" }), "info");
    try {
      const [data, error] = await DeleteRole(currentID);
      if (error) {
        setError(error);
      } else {
        showNotification(
          t("Role deleted successfully!", { ns: "roles" }),
          "success"
        );
        navigate(-1);
      }
    } catch (err) {
      setError({
        err: t("server.Server error.", { ns: "error" }),
        errCode: 500,
      });
    } finally {
      setDeleteBtnDisabled(false);
      removePopup(key);
    }
  };

  const tutorialSteps = [
    {
      target: null,
      title: t("Editing a role", { ns: "tutorial" }),
      message: t(
        "This page shows one role's full details and lets you change its permissions, priority, or delete it entirely.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "role-edit-name",
      title: t("Name", { ns: "tutorial" }),
      message: t("Rename the role here if you need to.", { ns: "tutorial" }),
    },
    {
      target: "role-edit-priority",
      title: t("Priority level", { ns: "tutorial" }),
      message: t(
        "Controls which roles this role is allowed to edit. Lower numbers are higher priority - a role can only edit roles with a lower priority (higher number) than its own.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "role-edit-color",
      title: t("Color", { ns: "tutorial" }),
      message: t(
        "The color shown next to this role wherever roles are listed.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "role-edit-permissions",
      title: t("Permissions", { ns: "tutorial" }),
      message: t(
        "Tick or untick actions to change exactly what employees with this role are allowed to do.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "role-edit-warehouses",
      title: t("Allowed warehouses", { ns: "tutorial" }),
      message: t(
        "Optional. Restrict this role to only see and work with specific warehouses. Leave empty to allow all warehouses.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "role-edit-apply-btn",
      title: t("Save your changes", { ns: "tutorial" }),
      message: t(
        "Once you're happy with your changes, click here to save them. This button only lights up once something has actually changed.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "role-edit-delete-btn",
      title: t("Delete", { ns: "tutorial" }),
      message: t(
        "Permanently deletes this role and removes it from every employee that has it - they'll lose whatever permissions it granted. This cannot be undone.",
        { ns: "tutorial" }
      ),
    },
  ];

  return (
    <PageWrapper tutorialId="roleEdit" tutorialSteps={tutorialSteps}>
      <PageLoader loading={loading} error={pageError}>
        <div className="page-content">
          <InputGroup className="container-bordered role-creation w-90 vertical mb-5 mt-4 h-500px">
            <InputField
              label={t("ID") + ":"}
              value={formData.RoleID}
              onChange={(val) => {
                handleChange("RoleID", val);
              }}
              placeholder={t("Example: 1001")}
              mandatory
              className="mb-2"
              readOnly={true}
            />
            <InputField
              label={t("Name") + ":"}
              value={formData.Name}
              onChange={(val) => {
                handleChange("Name", val);
              }}
              placeholder={t("Example: Manager", { ns: "roles" })}
              mandatory
              className="mb-2"
              tutorialId="role-edit-name"
            />
            <InputField
              label={t("Priority level") + ":"}
              value={formData.PriorityLevel}
              onChange={(val) => {
                handleChange("PriorityLevel", val);
              }}
              placeholder={t("Example: 1", { ns: "roles" })}
              mandatory
              description={t(
                "The priority level of a role determines which roles you can edit. The highest priority level is 0.",
                { ns: "roles" }
              )}
              className="mb-2"
              tutorialId="role-edit-priority"
            />
            <InputField
              label={t("Color") + ":"}
              type="color"
              value={formData.Color}
              onChange={(val) => {
                handleChange("Color", val);
              }}
              tutorialId="role-edit-color"
            />
            <MandatoryExplanation />
          </InputGroup>

          <Title className="mt-4">Permissions</Title>
          <div data-tutorial-id="role-edit-permissions">
            <PermissionView
              getPermissionsForUser={false}
              selectable
              setNewSelection={(val) => {
                handleChange("Permissions", val);
              }}
              initSelection={formData.Permissions}
            />
          </div>

          <div data-tutorial-id="role-edit-warehouses">
            <WarehousePicker
              className="w-90 mt-5"
              onChange={(val) => {
                handleChange("AllowedWarehouses", val);
              }}
              initWarehouses={formData.AllowedWarehouses}
            />
          </div>
          <span className="last-edited-by-bar w-90">
            <span className="sec-textcolor-span">{t("Last edited by:")} </span>
            &nbsp;
            {formData.LastEditedBy}
          </span>

          <div className="btn-container">
            <Button
              btnMargin="btn-default"
              btnStyle="btn-positive"
              onClick={OnApply}
              disabled={!hasChanged || editing}
              acceptEnter
              icon="apply"
              tutorialId="role-edit-apply-btn"
            >
              {t("Apply Changes")}
            </Button>
            <Button
              btnMargin="btn-default"
              btnStyle="btn-primary"
              onClick={() => navigate(-1)}
            >
              {t("Cancel")}
            </Button>
            <Button
              btnMargin="btn-default"
              btnStyle="btn-negative"
              icon="delete"
              onClick={() => showConfirmationPopup()}
              disabled={deleteBtnDisabled}
              tutorialId="role-edit-delete-btn"
            >
              {t("Delete")}
            </Button>
          </div>
        </div>
      </PageLoader>
    </PageWrapper>
  );
}

export default RoleEditPage;
